import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdCalendarToday,
    MdCloudUpload,
    MdEmail,
    MdImage,
    MdLocationOn,
    MdPerson,
    MdPhone,
} from 'react-icons/md'
import { useImageCrop } from '@/components/image-crop'
import { updateUser, uploadOwnPicture } from '@/lib/api/users/users-api'
import { updateCompany } from '@/lib/api/companies/companies-api'
import { CompanySegment } from '@/lib/api/common/interfaces/company.interface'
import { getFileUrl } from '@/lib/utils/file-url'
import { fetchPostalCode } from '@/lib/api/fetch-postal-code'
import { handleDateInputPaste } from '@/lib/utils/date-input'
import { isIndividualCompany } from '@/lib/person-type'
import { setCredentials } from '@/store/slices/authSlice'
import { useAppDispatch, useAppSelector, useAuth } from '@/store/hooks'
import { useLogoUpload } from '../use-logo-upload'
import { formatCpf, formatPhone, formatCep } from '../account-utils'

interface Props {
    /**
     * Chamado apos salvar. Quem decide para onde ir e o shell: a regra atual
     * leva para a aba de empresa, e e justamente ela que esta em revisao
     * (conta de pessoa fisica nao deveria cair em "Empresa").
     */
    onSaved?: () => void
}

/** Aba "Conta": dados do usuario logado e a logo da organizacao. */
export default function AccountTab({ onSaved }: Props) {
    const auth = useAppSelector(state => state.auth)
    const { user, isPartner } = useAuth()
    const dispatch = useAppDispatch()
    const { uploadingLogo, logoPreview, handleLogoUpload, logoCropDialog } =
        useLogoUpload()
    // A foto da conta é exibida redonda: corte quadrado com prévia circular.
    const pictureCrop = useImageCrop({
        title: 'Ajustar foto',
        aspect: 1,
        round: true,
        output: { maxWidth: 512 },
    })
    const logoInputRef = useRef<HTMLInputElement>(null)
    const pictureInputRef = useRef<HTMLInputElement>(null)

    // Conta de pessoa física: não existe aba "Empresa", então o segmento (que é
    // campo da Company) é editado aqui. Ver docs/person-type-pf-pj.md.
    const isCompanyIndividual = isIndividualCompany(user?.company)

    const [uploadingPicture, setUploadingPicture] = useState(false)
    const [picturePreview, setPicturePreview] = useState<string | null>(null)
    const [segment, setSegment] = useState<CompanySegment | ''>('')

    const [savingAccount, setSavingAccount] = useState(false)
    const [accountData, setAccountData] = useState({
        name: '',
        email: '',
        phone: '',
        cpf: '',
        birthDate: '',
        zipCode: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
    })
    const [loadingAccountCep, setLoadingAccountCep] = useState(false)

    useEffect(() => {
        if (!user) return

        setAccountData({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            cpf: user.cpf || '',
            birthDate: user.birthDate ? user.birthDate.split('T')[0] : '',
            zipCode: user.zipCode || '',
            street: user.street || '',
            number: user.number || '',
            complement: user.complement || '',
            neighborhood: user.neighborhood || '',
            city: user.city || '',
            state: user.state || '',
        })

        setSegment(((user.company as any)?.segment as CompanySegment) ?? '')
    }, [user])

    const handlePictureUpload = async (rawFile: File) => {
        const file = await pictureCrop.cropFile(rawFile)
        if (!file) return
        setPicturePreview(URL.createObjectURL(file))
        setUploadingPicture(true)
        try {
            const response = await uploadOwnPicture(file)

            if (response.success && response.result) {
                toast.success('Foto atualizada com sucesso')
                setPicturePreview(null)
                dispatch(
                    setCredentials({
                        ...auth,
                        user: {
                            ...user!,
                            picture: response.result.picture,
                        },
                    })
                )
            } else {
                toast.error(response.error || 'Erro ao enviar foto')
                setPicturePreview(null)
            }
        } catch {
            toast.error('Erro ao enviar foto')
            setPicturePreview(null)
        } finally {
            setUploadingPicture(false)
        }
    }

    const handleUpdateAccount = async () => {
        if (!user?.id) return

        if (!accountData.name.trim()) {
            toast.warning('Preencha o nome')
            return
        }

        if (!accountData.email.trim()) {
            toast.warning('Preencha o email')
            return
        }

        if (!accountData.cpf?.replace(/\D/g, '').trim()) {
            toast.warning('Preencha o CPF')
            return
        }

        setSavingAccount(true)
        try {
            const response = await updateUser(user.id, {
                name: accountData.name.trim(),
                email: accountData.email.trim(),
                phone: accountData.phone?.trim() || undefined,
                userType: user.userType || 'COMPANY',
                cpf: accountData.cpf?.replace(/\D/g, '') || undefined,
                birthDate: accountData.birthDate || undefined,
                zipCode: accountData.zipCode?.replace(/\D/g, '') || undefined,
                street: accountData.street?.trim() || undefined,
                number: accountData.number?.trim() || undefined,
                complement: accountData.complement?.trim() || undefined,
                neighborhood: accountData.neighborhood?.trim() || undefined,
                city: accountData.city?.trim() || undefined,
                state: accountData.state?.trim() || undefined,
            })

            if (response.success) {
                // O segmento é campo só da Company — o User não o tem, então não
                // vem no espelho do backend e precisa ser salvo daqui.
                let savedSegment: Record<string, unknown> = {}
                if (isCompanyIndividual && user.companyId && segment) {
                    const companyResponse = await updateCompany(
                        user.companyId,
                        { segment }
                    )
                    if (!companyResponse.success) {
                        toast.warning(
                            'Dados salvos, mas não foi possível atualizar a área de atuação'
                        )
                    } else {
                        savedSegment = { segment }
                    }
                }

                toast.success('Dados atualizados com sucesso')

                // Em PF o backend espelha o titular na Company. Refletir isso no
                // Redux aqui também é obrigatório, não cosmético: o
                // RegistrationGuard lê o endereço de `user.company`, e sem isso ele
                // continuaria bloqueando o painel cobrando o endereço que o usuário
                // acabou de preencher — até o próximo login.
                const mirroredCompany = isCompanyIndividual
                    ? {
                          name: accountData.name,
                          tradingName: accountData.name,
                          cpf: accountData.cpf,
                          phone: accountData.phone,
                          street: accountData.street,
                          number: accountData.number,
                          complement: accountData.complement,
                          neighborhood: accountData.neighborhood,
                          city: accountData.city,
                          state: accountData.state,
                          zipCode: accountData.zipCode,
                      }
                    : {}

                dispatch(
                    setCredentials({
                        ...auth,
                        user: {
                            ...user,
                            ...accountData,
                            company: user.company
                                ? ({
                                      ...user.company,
                                      ...mirroredCompany,
                                      ...savedSegment,
                                  } as any)
                                : user.company,
                        },
                    })
                )
                onSaved?.()
                document
                    .querySelector('main')
                    ?.scrollTo({ top: 0, behavior: 'smooth' })
                window.scrollTo({ top: 0, behavior: 'smooth' })
            } else {
                toast.error(response.error || 'Erro ao atualizar dados')
            }
        } catch (error) {
            toast.error('Erro ao atualizar dados')
        } finally {
            setSavingAccount(false)
        }
    }

    const handleAccountCepChange = async (cep: string) => {
        const cleanCep = cep.replace(/\D+/g, '')

        if (cleanCep.length !== 8) {
            return
        }

        setLoadingAccountCep(true)
        try {
            const address = await fetchPostalCode(cleanCep)

            setAccountData(prev => ({
                ...prev,
                zipCode: address.cep || prev.zipCode,
                street: address.logradouro || prev.street,
                neighborhood: address.bairro || prev.neighborhood,
                city: address.localidade || prev.city,
                state: address.uf || prev.state,
            }))
        } catch (error) {
            toast.error('CEP não encontrado')
        } finally {
            setLoadingAccountCep(false)
        }
    }

    return (
            <div className="flex flex-col gap-6">
                {isPartner && (
                    <div className="card bg-base-200 p-6 mt-2">
                        <h3 className="font-semibold text-base-content mb-4">
                            Logomarca da sua empresa
                        </h3>
                        <div className="flex items-center gap-6">
                            <div className="w-24 h-24 rounded-lg bg-base-100 border-2 border-dashed border-base-300 flex items-center justify-center overflow-hidden">
                                {logoPreview ||
                                user?.partner?.logoUrl ? (
                                    <img
                                        src={
                                            logoPreview ||
                                            getFileUrl(
                                                user?.partner
                                                    ?.logoUrl
                                            )
                                        }
                                        alt="Logo"
                                        className="max-w-full max-h-full object-contain"
                                    />
                                ) : (
                                    <MdImage className="text-3xl text-base-content/30" />
                                )}
                            </div>
                            <div className="flex flex-col gap-2">
                                <input
                                    ref={logoInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={e => {
                                        const file =
                                            e.target.files?.[0]
                                        // O preview sai do hook, já com o corte aplicado.
                                        if (file) handleLogoUpload(file)
                                        e.target.value = ''
                                    }}
                                />
                                <button
                                    className="btn btn-secondary btn-sm gap-2"
                                    onClick={() =>
                                        logoInputRef.current?.click()
                                    }
                                    disabled={uploadingLogo}
                                >
                                    {uploadingLogo ? (
                                        <span className="loading loading-spinner loading-sm"></span>
                                    ) : (
                                        <MdCloudUpload className="text-lg" />
                                    )}
                                    {uploadingLogo
                                        ? 'Enviando...'
                                        : 'Enviar Logo'}
                                </button>
                                <p className="text-xs text-base-content/50">
                                    PNG, JPG ou SVG. Tamanho
                                    máximo: 5MB.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="card bg-base-200 p-6 mt-2">
                    <h3 className="font-semibold text-base-content mb-4">
                        Sua foto
                    </h3>
                    <div className="flex items-center gap-6">
                        <div className="w-24 h-24 rounded-full bg-base-100 border-2 border-dashed border-base-300 flex items-center justify-center overflow-hidden">
                            {picturePreview || user?.picture ? (
                                <img
                                    src={
                                        picturePreview ||
                                        getFileUrl(user?.picture)
                                    }
                                    alt="Foto do usuário"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <MdPerson className="text-4xl text-base-content/30" />
                            )}
                        </div>
                        <div className="flex flex-col gap-2">
                            <input
                                ref={pictureInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={e => {
                                    const file = e.target.files?.[0]
                                    // O preview sai do corte, dentro do handler.
                                    if (file) handlePictureUpload(file)

                                    // Permite reenviar o MESMO arquivo (o input
                                    // não dispara change se o valor não muda).
                                    e.target.value = ''
                                }}
                            />
                            <button
                                className="btn btn-secondary btn-sm gap-2"
                                onClick={() => pictureInputRef.current?.click()}
                                disabled={uploadingPicture}
                            >
                                {uploadingPicture ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <MdCloudUpload className="text-lg" />
                                )}
                                {uploadingPicture
                                    ? 'Enviando...'
                                    : 'Enviar foto'}
                            </button>
                            <p className="text-xs text-base-content/50">
                                PNG ou JPG. Tamanho máximo: 5MB.
                            </p>
                        </div>
                    </div>
                </div>

                <h2 className="text-xl font-bold text-base-content">
                    Informações
                </h2>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Nome{' '}
                            <span className="text-error">
                                *
                            </span>
                        </span>
                    </label>
                    <div className="relative">
                        <MdPerson className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Nome completo"
                            className="input input-bordered w-full pl-10"
                            value={accountData.name}
                            onChange={e =>
                                setAccountData({
                                    ...accountData,
                                    name: e.target.value,
                                })
                            }
                        />
                    </div>
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Email{' '}
                            <span className="text-error">
                                *
                            </span>
                        </span>
                    </label>
                    <div className="relative">
                        <MdEmail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="email"
                            placeholder="email@exemplo.com"
                            className="input input-bordered w-full pl-10"
                            value={accountData.email}
                            onChange={e =>
                                setAccountData({
                                    ...accountData,
                                    email: e.target.value,
                                })
                            }
                        />
                    </div>
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Telefone{' '}
                            <span className="text-gray-400 text-sm">
                                (opcional)
                            </span>
                        </span>
                    </label>
                    <div className="relative">
                        <MdPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="tel"
                            placeholder="(00) 00000-0000"
                            className="input input-bordered w-full pl-10"
                            value={formatPhone(
                                accountData.phone
                            )}
                            onChange={e => {
                                const raw = e.target.value
                                    .replace(/\D/g, '')
                                    .slice(0, 11)
                                setAccountData({
                                    ...accountData,
                                    phone: raw,
                                })
                            }}
                        />
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="form-control w-full sm:w-1/2">
                        <label className="label">
                            <span className="label-text">
                                CPF
                            </span>
                        </label>
                        <input
                            type="text"
                            placeholder="000.000.000-00"
                            className="input input-bordered w-full"
                            value={formatCpf(accountData.cpf)}
                            onChange={e => {
                                const raw = e.target.value
                                    .replace(/\D/g, '')
                                    .slice(0, 11)
                                setAccountData({
                                    ...accountData,
                                    cpf: raw,
                                })
                            }}
                        />
                    </div>

                    <div className="form-control w-full sm:w-1/2">
                        <label className="label">
                            <span className="label-text">
                                Data de Nascimento
                            </span>
                        </label>
                        <div className="relative">
                            <MdCalendarToday className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                            <input
                                type="date"
                                className="input input-bordered w-full pl-10"
                                value={accountData.birthDate}
                                onChange={e =>
                                    setAccountData({
                                        ...accountData,
                                        birthDate:
                                            e.target.value,
                                    })
                                }
                                onPaste={e =>
                                    handleDateInputPaste(
                                        e,
                                        isoDate =>
                                            setAccountData({
                                                ...accountData,
                                                birthDate: isoDate,
                                            }),
                                    )
                                }
                            />
                        </div>
                    </div>
                </div>

                {/*
                    Área de atuação é campo da Company, mas conta de pessoa física
                    não tem aba "Empresa" — ela mora aqui. Decide o vocabulário do
                    sistema (em Saúde, "Clientes" viram "Pacientes") e quais telas
                    fazem sentido, então continua editável mesmo para quem só usa a
                    conta digital: nesse caso, "Outro".
                */}
                {isCompanyIndividual && (
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">Área de atuação</span>
                        </label>
                        <select
                            className="select select-bordered w-full"
                            value={segment}
                            onChange={e =>
                                setSegment(e.target.value as CompanySegment)
                            }
                        >
                            <option value="">Selecione a área</option>
                            <option value={CompanySegment.HEALTH}>Saúde</option>
                            <option value={CompanySegment.BEAUTY}>
                                Beleza e Estética
                            </option>
                            <option value={CompanySegment.FITNESS}>
                                Academia e Fitness
                            </option>
                            <option value={CompanySegment.PET_SHOP}>
                                Pet Shop e Veterinária
                            </option>
                            <option value={CompanySegment.DIRECT_SALE}>
                                Venda com preços fixos
                            </option>
                            <option value={CompanySegment.QUOTE_BASED_SALE}>
                                Venda com orçamento
                            </option>
                            <option value={CompanySegment.OTHER}>Outro</option>
                        </select>
                    </div>
                )}

                <div className="divider">
                    <MdLocationOn className="text-lg text-base-content/50" />
                    <span className="text-sm text-base-content/70">
                        Endereço
                    </span>
                </div>

                <div className="form-control w-full sm:w-1/3">
                    <label className="label">
                        <span className="label-text">CEP</span>
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="00000-000"
                            className="input input-bordered w-full"
                            value={formatCep(
                                accountData.zipCode
                            )}
                            onChange={e => {
                                const raw = e.target.value
                                    .replace(/\D/g, '')
                                    .slice(0, 8)
                                setAccountData({
                                    ...accountData,
                                    zipCode: raw,
                                })
                                if (raw.length === 8)
                                    handleAccountCepChange(raw)
                            }}
                        />
                        {loadingAccountCep && (
                            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 loading loading-spinner loading-sm"></span>
                        )}
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="form-control w-full sm:flex-1">
                        <label className="label">
                            <span className="label-text">
                                Rua
                            </span>
                        </label>
                        <input
                            type="text"
                            placeholder="Rua, Avenida..."
                            className="input input-bordered w-full"
                            value={accountData.street}
                            onChange={e =>
                                setAccountData({
                                    ...accountData,
                                    street: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div className="form-control w-full sm:w-32">
                        <label className="label">
                            <span className="label-text">
                                Número
                            </span>
                        </label>
                        <input
                            type="text"
                            placeholder="Nº"
                            className="input input-bordered w-full"
                            value={accountData.number}
                            onChange={e =>
                                setAccountData({
                                    ...accountData,
                                    number: e.target.value,
                                })
                            }
                        />
                    </div>
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Complemento
                        </span>
                    </label>
                    <input
                        type="text"
                        placeholder="Apto, Sala, Bloco..."
                        className="input input-bordered w-full"
                        value={accountData.complement}
                        onChange={e =>
                            setAccountData({
                                ...accountData,
                                complement: e.target.value,
                            })
                        }
                    />
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Bairro
                        </span>
                    </label>
                    <input
                        type="text"
                        placeholder="Bairro"
                        className="input input-bordered w-full"
                        value={accountData.neighborhood}
                        onChange={e =>
                            setAccountData({
                                ...accountData,
                                neighborhood: e.target.value,
                            })
                        }
                    />
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="form-control w-full sm:flex-1">
                        <label className="label">
                            <span className="label-text">
                                Cidade
                            </span>
                        </label>
                        <input
                            type="text"
                            placeholder="Cidade"
                            className="input input-bordered w-full"
                            value={accountData.city}
                            onChange={e =>
                                setAccountData({
                                    ...accountData,
                                    city: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div className="form-control w-full sm:w-32">
                        <label className="label">
                            <span className="label-text">
                                UF
                            </span>
                        </label>
                        <input
                            type="text"
                            placeholder="UF"
                            className="input input-bordered w-full"
                            maxLength={2}
                            value={accountData.state}
                            onChange={e =>
                                setAccountData({
                                    ...accountData,
                                    state: e.target.value.toUpperCase(),
                                })
                            }
                        />
                    </div>
                </div>

                <button
                    className="btn btn-secondary w-full"
                    onClick={handleUpdateAccount}
                    disabled={savingAccount}
                >
                    {savingAccount ? (
                        <span className="loading loading-spinner"></span>
                    ) : (
                        'Salvar Alterações'
                    )}
                </button>
                {logoCropDialog}
                {pictureCrop.cropDialog}
            </div>
    )
}
